import BootcampChallengeDialog from "@Components/game/challenges/bootcamp-challenge-dialog";
import BootcampCompletionDialog from "@Components/game/challenges/bootcamp-completion-dialog";
import ChallengeCardSimple from "@Components/game/challenges/cards/challenge-card-simple";
import { BybBackground } from "@Components/game/common";
import SessionResultsLoadingOverlay from "@Components/game/common/session-results-loading-overlay";
import ContainerWrapper from "@Components/generic/container-wrapper";
import { BootcampChallengeStep } from "@Constants/bootcamp";
import AnalyticsEvents from "@Enums/AnalyticsEvents";
import ChallengeStatus from "@Enums/ChallengeStatus";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useBootcamp from "@hooks/game/bootcamp/use-bootcamp";
import useBootcampWelcomeGate from "@hooks/game/bootcamp/use-bootcamp-welcome-gate";
import useI18n from "@i18n";
import { Box, Grid, Portal, Stack, Typography } from "@mui/material";
import { ChallengeResponse } from "@Shared/schemas/challenges/challengeSchemas";
import { useState } from "react";

const BOOTCAMP_BACKGROUND_URL = "https://challenge-thumbs.b-cdn.net/byb-backgrounds/bootcamp.png?width=1440&height=600&quality=100&format=webp";
const BOOTCAMP_CHALLENGE_COUNT = 7;

const isChallengeAvailable = (challenge: ChallengeResponse, userRating: number) =>
  challenge.status !== ChallengeStatus.COMPLETED && (!challenge.unlocked_at || userRating >= challenge.unlocked_at);

const Bootcamp = () => {
  const { globalState } = useGlobalContext();
  const { track } = useAnalytics();
  const { i18n } = useI18n();
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeResponse | null>(null);

  useBootcampWelcomeGate();

  const {
    challenges,
    completionResult,
    isCompletingSession,
    isWebInputBlocked,
    startChallenge,
    closeCompletionResult,
    viewFullResults,
    retryChallenge,
  } = useBootcamp(globalState.user.id);

  const allChallengesCompleted =
    challenges.length === BOOTCAMP_CHALLENGE_COUNT && challenges.every(challenge => challenge.status === ChallengeStatus.COMPLETED);

  const openChallenge = (challenge: ChallengeResponse) => setSelectedChallenge(challenge);
  const closeChallenge = () => setSelectedChallenge(null);

  const launchChallenge = (challenge: ChallengeResponse) => {
    const completedSteps = challenges.filter((candidate: ChallengeResponse) => candidate.status === ChallengeStatus.COMPLETED).length;
    closeChallenge();
    startChallenge(challenge, completedSteps);
  };

  const playChallengePreview = (challenge: ChallengeResponse) => {
    track(AnalyticsEvents.challenge_preview_play_clicked, { user_rating: globalState.user.userRating });
    launchChallenge(challenge);
  };

  const playChallengeInVideo = (challenge: ChallengeResponse) => {
    track(AnalyticsEvents.video_player_play_challenge_clicked, { user_rating: globalState.user.userRating });
    launchChallenge(challenge);
  };

  const watchChallengeVideo = (challenge: ChallengeResponse, title: string) => {
    if (!challenge.video_url) return;

    closeChallenge();
    globalState.app.events.showVideo.emit({
      videoId: challenge.video_url,
      title,
      onContinue: () => playChallengeInVideo(challenge),
    });
  };

  return (
    <ContainerWrapper id="BOOTCAMP" fullHeight sx={{ position: "relative", overflow: "hidden" }}>
      <BybBackground imageUrl={BOOTCAMP_BACKGROUND_URL} />

      <Stack paddingTop={20} />

      <Stack alignItems="center" spacing={1.5}>
        <Typography variant="text7xl800" fontWeight={700} color="common.white" textAlign={"center"}>
          {i18n(allChallengesCompleted ? "bootcamp.finished.title" : "bootcamp.home.title")}
        </Typography>
        <Typography variant="text1xl400" color="common.white" textAlign={"center"} width={"40%"}>
          {i18n(allChallengesCompleted ? "bootcamp.finished.description" : "bootcamp.home.description")}
        </Typography>
      </Stack>

      <Box sx={{ position: "relative", zIndex: 1, mt: 8, px: 3 }}>
        <Grid container spacing={3}>
          {challenges
            .slice(0, BOOTCAMP_CHALLENGE_COUNT)
            .map((challenge: ChallengeResponse, index: number, bootcampChallenges: ChallengeResponse[]) => {
              const nextAvailableIndex = bootcampChallenges.findIndex(candidate => isChallengeAvailable(candidate, globalState.user.userRating));

              return (
                <Grid key={challenge.id} item xs={12} sm={6} md={3}>
                  <ChallengeCardSimple
                    challenge={challenge}
                    size="small"
                    fullWidth
                    forceMaxWidth={false}
                    animatedBorder={index === nextAvailableIndex}
                    onClick={() => openChallenge(challenge)}
                  />
                </Grid>
              );
            })}
        </Grid>
      </Box>

      {selectedChallenge && (
        <BootcampChallengeDialog
          challenge={selectedChallenge}
          open
          onClose={closeChallenge}
          onStart={() => playChallengePreview(selectedChallenge)}
          onWatchVideo={watchChallengeVideo}
        />
      )}

      {completionResult && (
        <BootcampCompletionDialog
          challengeStep={completionResult.challengeStep}
          metricScore={completionResult.metricScore}
          metricTarget={completionResult.metricTarget}
          completedSteps={completionResult.completedSteps}
          failed={completionResult.failed}
          open
          onClose={closeCompletionResult}
          onContinue={
            completionResult.challengeStep === BootcampChallengeStep.FINAL && !completionResult.failed ? viewFullResults : closeCompletionResult
          }
          onRetry={retryChallenge}
        />
      )}

      <SessionResultsLoadingOverlay open={isCompletingSession} />

      {/* Block web input when a challenge is being created or completed */}
      {isWebInputBlocked && (
        <Portal>
          <Box
            aria-hidden
            sx={theme => ({
              position: "fixed",
              inset: 0,
              zIndex: theme.zIndex.modal + 10,
              pointerEvents: "auto",
            })}
          />
        </Portal>
      )}
    </ContainerWrapper>
  );
};

export default Bootcamp;
